import { useEffect, useMemo, useState } from "react";
import {
    motion,
    AnimatePresence,
    useMotionValue,
    useTransform,
    animate,
} from "framer-motion";
import {
    Check,
    Languages,
    Loader2,
    RefreshCw,
    ShieldCheck,
    Sparkles,
    Wallet,
    X as XIcon,
} from "lucide-react";

import {
    Sheet,
    SheetContent,
    SheetHeader,
    SheetTitle,
    SheetDescription,
} from "@/components/ui/sheet";
import { useSession } from "@/contexts/SessionContext";
import { scoreColor } from "@/lib/adsum-colors";

const PILLAR_LABEL = {
    brand_alignment: "Brand alignment",
    conversion_optimization: "Conversion",
    distribution_fit: "Distribution",
};

export default function AdsumDrawer({
    open,
    onOpenChange,
    phase,
    review,
    error,
    applied,
    assetId,
    title,
    caption,
    hashtags,
    scheduledFor,
    platforms,
    onRun,
    onApplyTweak,
}) {
    const { balance, profile, openDrawer, tier } = useSession();
    const [confirmingRerun, setConfirmingRerun] = useState(false);
    const [flash, setFlash] = useState(false);
    // Phase 4-b Task D — bilinguality. Pro-only toggle, in-memory only.
    const [language, setLanguage] = useState("en");
    const showLanguageToggle = tier === "pro";

    const hasAsset = !!assetId;
    const hasTitle = !!(title && title.trim());
    const hasCaption = !!(caption && caption.trim());
    const hasSchedule = !!(
        scheduledFor && new Date(scheduledFor).getTime() > Date.now()
    );
    const hasNiche = !!(profile?.niche && profile.niche.trim());
    const allPrereqs = hasAsset && hasTitle && hasCaption && hasSchedule && hasNiche;
    const lowBalance = balance < 5;

    // Mint flash overlay when "Apply tweak" lands
    useEffect(() => {
        if (!applied) return;
        setFlash(true);
        const t = setTimeout(() => setFlash(false), 700);
        return () => clearTimeout(t);
    }, [applied]);

    const handleRun = () => {
        if (!allPrereqs || lowBalance || phase === "loading") return;
        onRun({
            asset_id: assetId,
            selected_title: title,
            selected_caption: caption,
            selected_hashtags: hashtags || [],
            platforms: platforms || [],
            scheduled_for: scheduledFor,
            language,
        });
    };

    const handleRerun = () => {
        if (confirmingRerun) {
            setConfirmingRerun(false);
            handleRun();
        } else {
            setConfirmingRerun(true);
            setTimeout(() => setConfirmingRerun(false), 3500);
        }
    };

    const handleConfigureNiche = () => {
        onOpenChange(false);
        setTimeout(() => openDrawer("profile"), 220);
    };

    const handleApply = () => {
        if (!review?.actionable_tweak) return;
        onApplyTweak?.({
            rewritten_title: review.actionable_tweak.rewritten_title,
            rewritten_caption: review.actionable_tweak.rewritten_caption,
            rewritten_hashtags:
                review.actionable_tweak.rewritten_hashtags || [],
        });
    };

    return (
        <Sheet open={open} onOpenChange={onOpenChange}>
            <SheetContent
                side="right"
                className="w-full overflow-y-auto overflow-x-hidden border-l border-[#1F2A40] bg-[#0B0F19] p-0 text-[#E8ECF5] sm:max-w-[440px]"
                data-testid="adsum-drawer"
            >
                <AnimatePresence>
                    {flash && (
                        <motion.div
                            key="flash"
                            aria-hidden
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 0.45 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.6 }}
                            className="pointer-events-none absolute inset-0 z-20"
                            style={{
                                background:
                                    "radial-gradient(circle at 50% 50%, rgba(0,245,212,0.55) 0%, transparent 70%)",
                            }}
                            data-testid="adsum-drawer-flash"
                        />
                    )}
                </AnimatePresence>

                <SheetHeader className="sticky top-0 z-10 flex flex-col gap-2 border-b border-[#1F2A40] bg-[#0B0F19]/95 px-6 py-4 backdrop-blur">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <ShieldCheck className="h-4 w-4 text-[#00F5D4]" />
                            <span className="font-mono text-[10px] uppercase tracking-[0.32em] text-[#8A93A6]">
                                agent 3 · adsum
                            </span>
                        </div>
                        <button
                            type="button"
                            onClick={() => onOpenChange(false)}
                            className="rounded-md p-1.5 text-[#8A93A6] transition-colors hover:bg-[#111726] hover:text-[#E8ECF5]"
                            aria-label="Close"
                            data-testid="adsum-drawer-close"
                        >
                            <XIcon className="h-4 w-4" />
                        </button>
                    </div>
                    <SheetTitle className="text-xl font-medium tracking-tight text-[#E8ECF5]">
                        Pre-publish QC review
                    </SheetTitle>
                    <SheetDescription className="text-sm text-[#8A93A6]">
                        Brand-Director-grade audit of your title, caption,
                        platforms, and schedule.
                    </SheetDescription>
                </SheetHeader>

                <div className="relative flex flex-col gap-5 px-6 py-6">
                    <PrereqsStrip
                        hasAsset={hasAsset}
                        hasTitle={hasTitle}
                        hasCaption={hasCaption}
                        hasSchedule={hasSchedule}
                        hasNiche={hasNiche}
                        onConfigureNiche={handleConfigureNiche}
                    />

                    {showLanguageToggle && (
                        <LanguageToggle
                            value={language}
                            onChange={setLanguage}
                        />
                    )}

                    <AnimatePresence mode="wait">
                        {(phase === "idle" || phase === "error") && (
                            <IdleSection
                                key="idle"
                                error={phase === "error" ? error : null}
                                allPrereqs={allPrereqs}
                                lowBalance={lowBalance}
                                onRun={handleRun}
                                onTopUp={() => openDrawer("topup")}
                            />
                        )}

                        {phase === "loading" && <LoadingSection key="loading" />}

                        {phase === "insufficient" && (
                            <InsufficientSection
                                key="insufficient"
                                balance={balance}
                                onTopUp={() => openDrawer("topup")}
                            />
                        )}

                        {phase === "result" && review && (
                            <ResultSection
                                key="result"
                                review={review}
                                applied={applied}
                                onApply={handleApply}
                                onRerun={handleRerun}
                                confirmingRerun={confirmingRerun}
                                allPrereqs={allPrereqs}
                                lowBalance={lowBalance}
                            />
                        )}
                    </AnimatePresence>
                </div>
            </SheetContent>
        </Sheet>
    );
}

/* ---------------- subviews ---------------- */

function LanguageToggle({ value, onChange }) {
    const opts = [
        { id: "en", label: "EN", aria: "English" },
        { id: "es", label: "ES", aria: "Español" },
    ];
    return (
        <div
            className="flex items-center justify-between gap-3 rounded-xl border border-[#1F2A40] bg-[#111726] px-4 py-2.5"
            data-testid="adsum-language-toggle"
        >
            <div className="flex items-center gap-2">
                <Languages className="h-3.5 w-3.5 text-[#00F5D4]" />
                <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#8A93A6]">
                    output language
                </span>
            </div>
            <div className="relative inline-flex items-center rounded-full border border-[#1F2A40] bg-[#0B0F19] p-0.5">
                {opts.map((o) => {
                    const active = o.id === value;
                    return (
                        <button
                            key={o.id}
                            type="button"
                            onClick={() => onChange(o.id)}
                            aria-pressed={active}
                            aria-label={o.aria}
                            data-testid={`adsum-language-${o.id}`}
                            data-active={active ? "true" : "false"}
                            className={[
                                "relative z-10 inline-flex h-6 min-w-[36px] items-center justify-center rounded-full px-2 font-mono text-[10px] font-semibold uppercase tracking-[0.18em] transition-colors duration-200",
                                active
                                    ? "text-[#0B0F19]"
                                    : "text-[#8A93A6] hover:text-[#E8ECF5]",
                            ].join(" ")}
                        >
                            {active && (
                                <motion.span
                                    layoutId="adsum-language-pill"
                                    className="absolute inset-0 rounded-full bg-[#00F5D4]"
                                    style={{
                                        boxShadow:
                                            "0 0 12px rgba(0,245,212,0.5)",
                                    }}
                                    transition={{
                                        type: "spring",
                                        bounce: 0.18,
                                        duration: 0.4,
                                    }}
                                />
                            )}
                            <span className="relative z-10">{o.label}</span>
                        </button>
                    );
                })}
            </div>
        </div>
    );
}

function PrereqsStrip({
    hasAsset,
    hasTitle,
    hasCaption,
    hasSchedule,
    hasNiche,
    onConfigureNiche,
}) {
    const items = [
        { key: "asset", label: "Asset uploaded", ok: hasAsset },
        { key: "title", label: "Title selected", ok: hasTitle },
        { key: "caption", label: "Caption selected", ok: hasCaption },
        { key: "schedule", label: "Date + time in future", ok: hasSchedule },
        {
            key: "niche",
            label: "Niche configured",
            ok: hasNiche,
            onFix: onConfigureNiche,
        },
    ];

    return (
        <div
            className="flex flex-col gap-2 rounded-xl border border-[#1F2A40] bg-[#111726] p-4"
            data-testid="adsum-prereqs-strip"
        >
            <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#8A93A6]">
                prerequisites
            </span>
            <div className="flex flex-col gap-1.5">
                {items.map(({ key, label, ok, onFix }) => (
                    <div
                        key={key}
                        className="flex items-center justify-between gap-3 text-sm"
                        data-testid={`adsum-prereq-${key}`}
                        data-ok={ok ? "true" : "false"}
                    >
                        <div className="flex min-w-0 items-center gap-2">
                            <span
                                className={[
                                    "inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[10px] font-semibold",
                                    ok
                                        ? "bg-[#00F5D4]/15 text-[#00F5D4]"
                                        : "bg-[#FF5470]/15 text-[#FF5470]",
                                ].join(" ")}
                            >
                                {ok ? (
                                    <Check className="h-3 w-3" strokeWidth={3} />
                                ) : (
                                    <XIcon className="h-3 w-3" strokeWidth={3} />
                                )}
                            </span>
                            <span
                                className={ok ? "text-[#E8ECF5]" : "text-[#8A93A6]"}
                            >
                                {label}
                            </span>
                        </div>
                        {!ok && onFix && (
                            <button
                                type="button"
                                onClick={onFix}
                                className="font-mono text-[10px] uppercase tracking-wider text-[#00F5D4] transition-colors hover:underline"
                                data-testid={`adsum-prereq-fix-${key}`}
                            >
                                Configure
                            </button>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
}

function IdleSection({ error, allPrereqs, lowBalance, onRun, onTopUp }) {
    let helper = null;
    if (!allPrereqs) {
        helper =
            "Complete the missing items in the compose panel to run a review.";
    } else if (lowBalance) {
        helper = "Balance is below 5 NXT — top up to continue.";
    }

    const ctaLabel =
        lowBalance && allPrereqs ? "Top up NXT" : "Run QC Review (−5 NXT)";
    const handleClick = lowBalance && allPrereqs ? onTopUp : onRun;
    const disabled = !allPrereqs;

    return (
        <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className="flex flex-col gap-3"
        >
            <button
                type="button"
                onClick={handleClick}
                disabled={disabled}
                className="group relative inline-flex w-full items-center justify-center gap-2 overflow-hidden rounded-lg bg-gradient-to-r from-[#2F58CD] to-[#2F58CD] px-4 py-3 text-sm font-medium text-white transition-all hover:shadow-[0_0_24px_rgba(0,245,212,0.4)] disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:shadow-none"
                data-testid="adsum-run-btn"
            >
                <span className="relative z-10 inline-flex items-center gap-2">
                    <Sparkles className="h-3.5 w-3.5" />
                    {ctaLabel}
                </span>
                <span
                    aria-hidden
                    className="absolute inset-0 bg-gradient-to-r from-transparent via-[#00F5D4]/35 to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100"
                />
            </button>
            {helper && (
                <p
                    className="font-mono text-[11px] italic text-[#8A93A6]"
                    data-testid="adsum-helper-text"
                >
                    {helper}
                </p>
            )}
            {error && (
                <p
                    className="text-xs text-[#FF5470]"
                    data-testid="adsum-error-message"
                >
                    {error}
                </p>
            )}
        </motion.div>
    );
}

function LoadingSection() {
    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="flex flex-col gap-3"
            data-testid="adsum-loading"
        >
            <div className="flex items-center gap-2">
                <Loader2 className="h-3.5 w-3.5 animate-spin text-[#00F5D4]" />
                <span className="font-mono text-xs uppercase tracking-[0.32em] text-[#00F5D4]">
                    Adsum is auditing your post…
                </span>
            </div>
            <div className="flex flex-col gap-2">
                {[0, 1, 2].map((i) => (
                    <motion.div
                        key={i}
                        initial={{ opacity: 0, x: -8 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: i * 0.12, duration: 0.4 }}
                        className="sp-shimmer relative h-7 overflow-hidden rounded-md border border-[#1F2A40] bg-[#111726]"
                    />
                ))}
            </div>
        </motion.div>
    );
}

function InsufficientSection({ balance, onTopUp }) {
    return (
        <motion.div
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className="flex flex-col gap-3 rounded-xl border border-[#FF5470]/30 bg-[#111726] p-4"
            data-testid="adsum-insufficient"
        >
            <div className="flex items-center gap-2">
                <Wallet className="h-4 w-4 text-[#FF5470]" />
                <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#FF5470]">
                    nexavault is low
                </span>
            </div>
            <p className="text-sm text-[#8A93A6]">
                Adsum needs 5 NXT per audit. You have{" "}
                <span className="font-mono">{balance}</span> NXT.
            </p>
            <button
                type="button"
                onClick={onTopUp}
                className="self-start rounded-lg bg-gradient-to-r from-[#2F58CD] to-[#2F58CD] px-3 py-1.5 text-xs font-medium text-white transition-all hover:shadow-[0_0_20px_rgba(0,245,212,0.35)]"
                data-testid="adsum-topup-btn"
            >
                Top up $5 → +500 NXT
            </button>
        </motion.div>
    );
}

function ResultSection({
    review,
    applied,
    onApply,
    onRerun,
    confirmingRerun,
    allPrereqs,
    lowBalance,
}) {
    const score = review.brand_score;
    const color = useMemo(() => scoreColor(score), [score]);

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="flex flex-col gap-5"
            data-testid="adsum-result"
        >
            <div className="flex flex-col items-center gap-4 sm:flex-row sm:items-start">
                <ScoreRing score={score} color={color} />
                <div className="flex min-w-0 flex-1 flex-col gap-3">
                    <p
                        className="text-sm leading-relaxed text-[#E8ECF5]"
                        data-testid="adsum-critique"
                    >
                        {review.critique}
                    </p>
                    <PillarBars pillars={review.pillar_scores} />
                </div>
            </div>

            <TweakBlock
                tweak={review.actionable_tweak}
                applied={applied}
                onApply={onApply}
            />

            <div className="flex items-center justify-between border-t border-[#1F2A40]/60 pt-3">
                <button
                    type="button"
                    onClick={onRerun}
                    disabled={!allPrereqs || lowBalance}
                    className="inline-flex items-center gap-1.5 rounded-md px-2 py-1 font-mono text-[10px] uppercase tracking-wider text-[#8A93A6] transition-colors hover:text-[#00F5D4] disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:text-[#8A93A6]"
                    data-testid="adsum-rerun-btn"
                >
                    <RefreshCw className="h-3 w-3" />
                    {confirmingRerun
                        ? "Click again — costs 5 NXT"
                        : "Re-run audit"}
                </button>
                <span
                    className="font-mono text-[10px] italic text-[#8A93A6]"
                    data-testid="adsum-balance-chip"
                >
                    balance · {review.balance_after} NXT
                </span>
            </div>
        </motion.div>
    );
}

function ScoreRing({ score, color }) {
    const RADIUS = 52;
    const CIRC = 2 * Math.PI * RADIUS;
    const motionVal = useMotionValue(0);
    const dashOffset = useTransform(motionVal, (v) => CIRC * (1 - v / 100));
    const rounded = useTransform(motionVal, (v) => Math.round(v));
    const [display, setDisplay] = useState(0);

    useEffect(() => {
        motionVal.set(0);
        setDisplay(0);
        const controls = animate(motionVal, score, {
            duration: 0.85,
            ease: [0.16, 1, 0.3, 1],
        });
        const unsub = rounded.on("change", setDisplay);
        return () => {
            controls.stop();
            unsub();
        };
        // motionVal + rounded are stable framer-motion refs — safe to exclude.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [score]);

    return (
        <div
            className="relative flex shrink-0 items-center justify-center"
            data-testid="adsum-score-ring"
            data-score={score}
            style={{ width: 132, height: 132 }}
        >
            <svg viewBox="0 0 120 120" className="h-full w-full -rotate-90">
                <circle
                    cx="60"
                    cy="60"
                    r={RADIUS}
                    stroke="#1F2A40"
                    strokeWidth="8"
                    fill="none"
                />
                <motion.circle
                    cx="60"
                    cy="60"
                    r={RADIUS}
                    stroke={color}
                    strokeWidth="8"
                    strokeLinecap="round"
                    fill="none"
                    strokeDasharray={CIRC}
                    style={{
                        strokeDashoffset: dashOffset,
                        filter: `drop-shadow(0 0 6px ${color})`,
                    }}
                />
            </svg>
            <div className="absolute flex flex-col items-center">
                <span
                    className="font-medium tabular-nums leading-none tracking-tight"
                    style={{ color, fontSize: 38 }}
                    data-testid="adsum-score-value"
                >
                    {display}
                </span>
                <span className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-[#8A93A6]">
                    / 100
                </span>
            </div>
        </div>
    );
}

function PillarBars({ pillars }) {
    return (
        <div className="flex flex-col gap-2" data-testid="adsum-pillars">
            {["brand_alignment", "conversion_optimization", "distribution_fit"].map(
                (key, i) => {
                    const val = pillars?.[key] ?? 0;
                    const color = scoreColor(val);
                    return (
                        <div
                            key={key}
                            className="flex items-center gap-3"
                            data-testid={`adsum-pillar-${key}`}
                        >
                            <span className="w-24 shrink-0 font-mono text-[10px] uppercase tracking-wider text-[#8A93A6]">
                                {PILLAR_LABEL[key]}
                            </span>
                            <div className="relative h-1.5 flex-1 overflow-hidden rounded-full bg-[#1A2235]">
                                <motion.div
                                    initial={{ width: 0 }}
                                    animate={{ width: `${val}%` }}
                                    transition={{
                                        duration: 0.6,
                                        delay: 0.85 + i * 0.1,
                                        ease: [0.16, 1, 0.3, 1],
                                    }}
                                    className="absolute inset-y-0 left-0 rounded-full"
                                    style={{
                                        background: color,
                                        boxShadow: `0 0 8px ${color}`,
                                    }}
                                />
                            </div>
                            <span
                                className="w-8 shrink-0 text-right font-mono text-xs tabular-nums"
                                style={{ color }}
                                data-testid={`adsum-pillar-value-${key}`}
                            >
                                {val}
                            </span>
                        </div>
                    );
                }
            )}
        </div>
    );
}

function TweakBlock({ tweak, applied, onApply }) {
    return (
        <motion.div
            initial={{ opacity: 0, y: 6 }}
            animate={{
                opacity: 1,
                y: 0,
                boxShadow: applied
                    ? "0 0 20px rgba(0,245,212,0.15)"
                    : [
                          "0 0 0 rgba(0,245,212,0)",
                          "0 0 28px rgba(0,245,212,0.32)",
                          "0 0 16px rgba(0,245,212,0.12)",
                      ],
            }}
            transition={{ duration: 1.2, delay: 0.7 }}
            className="flex flex-col gap-3 rounded-xl border border-[#00F5D4]/40 bg-[#0B0F19] p-4"
            data-testid="adsum-tweak"
        >
            <div className="flex flex-col gap-1">
                <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#00F5D4]">
                    Adsum recommends
                </span>
                <p
                    className="text-base font-medium leading-snug text-[#E8ECF5]"
                    data-testid="adsum-tweak-summary"
                >
                    {tweak.summary}
                </p>
            </div>

            <div className="flex flex-col gap-1.5 rounded-md border border-[#1F2A40] bg-[#111726] p-3 font-mono text-xs leading-relaxed text-[#8A93A6]">
                <div data-testid="adsum-tweak-title-preview">
                    <span className="text-[#00F5D4]">Title →</span>{" "}
                    <span className="text-[#E8ECF5]">
                        {tweak.rewritten_title}
                    </span>
                </div>
                <div data-testid="adsum-tweak-caption-preview">
                    <span className="text-[#00F5D4]">Caption →</span>{" "}
                    <span className="text-[#E8ECF5]">
                        {tweak.rewritten_caption.length > 140
                            ? tweak.rewritten_caption.slice(0, 140) + "…"
                            : tweak.rewritten_caption}
                    </span>
                </div>
                {tweak.rewritten_hashtags?.length > 0 && (
                    <div data-testid="adsum-tweak-hashtags-preview">
                        <span className="text-[#00F5D4]">Hashtags →</span>{" "}
                        <span className="text-[#E8ECF5]">
                            {tweak.rewritten_hashtags.join(" ")}
                        </span>
                    </div>
                )}
            </div>

            {applied ? (
                <motion.div
                    initial={{ scale: 0.92, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ duration: 0.4 }}
                    className="inline-flex items-center gap-2 self-start rounded-md border border-[#00F5D4]/40 bg-[#00F5D4]/10 px-3 py-1.5 text-xs font-medium text-[#00F5D4]"
                    data-testid="adsum-applied-state"
                >
                    <Check className="h-3.5 w-3.5" strokeWidth={3} />
                    Tweak applied
                </motion.div>
            ) : (
                <button
                    type="button"
                    onClick={onApply}
                    className="group relative inline-flex w-fit items-center gap-2 overflow-hidden rounded-lg bg-gradient-to-r from-[#00F5D4] to-[#00F5D4] px-4 py-2 text-sm font-semibold text-[#0B0F19] transition-all hover:shadow-[0_0_24px_rgba(0,245,212,0.5)]"
                    data-testid="adsum-apply-btn"
                >
                    <span className="relative z-10 inline-flex items-center gap-2">
                        <Sparkles className="h-3.5 w-3.5" />
                        Apply tweak
                    </span>
                </button>
            )}
        </motion.div>
    );
}
